import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AlertTriangle, Bell, X, CheckCircle } from 'lucide-react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

const POLL_INTERVAL_MS = 30_000; // poll every 30 seconds

/**
 * NotificationAlert
 *
 * Renders a full-page blocking overlay when there are unread notifications.
 * The user must acknowledge each notification one-by-one before using the app.
 * Exported so App.js can mount it above all page content.
 */
const NotificationAlert = ({ onUnreadCountChange }) => {
  const { token, isAuthenticated, user } = useAuth();
  const [queue, setQueue] = useState([]);   // unread notifications
  const [dismissed, setDismissed] = useState(false); // user cleared current batch
  const pollRef = useRef(null);

  const fetchUnread = useCallback(async () => {
    if (!isAuthenticated || !token || user?.role === 'admin') return;
    try {
      const res = await api.get('/notifications/my', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const items = Array.isArray(res.data) ? res.data : [];
      setQueue(items);
      if (typeof onUnreadCountChange === 'function') {
        onUnreadCountChange(items.length);
      }
      if (items.length > 0) {
        setDismissed(false); // re-open overlay if new alerts arrive
      }
    } catch (err) {
      // Silently ignore polling errors
    }
  }, [isAuthenticated, token, onUnreadCountChange]);

  // Initial fetch + polling
  useEffect(() => {
    fetchUnread();
    pollRef.current = setInterval(fetchUnread, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchUnread]);

  const markRead = async (notification) => {
    try {
      await api.post(
        `/notifications/${notification.id}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      // Ignore read-mark errors – the notification will be re-shown on next poll
    }
  };

  const handleAcknowledge = async () => {
    const current = queue[0];
    if (!current) return;

    await markRead(current);
    const remaining = queue.slice(1);
    setQueue(remaining);
    if (typeof onUnreadCountChange === 'function') {
      onUnreadCountChange(remaining.length);
    }
    if (remaining.length === 0) {
      setDismissed(true);
    }
  };

  // Nothing to show
  if (!isAuthenticated || user?.role === 'admin' || dismissed || queue.length === 0) return null;

  const current = queue[0];
  const isHigh = current.priority === 'high';
  const isRejection = current.type === 'approval_rejected';

  return (
    /* Full-page blocking overlay */
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}
    >
      <div
        className={`relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden
          ${isHigh ? 'border-t-4 border-red-500' : 'border-t-4 border-blue-500'}`}
      >
        {/* Header */}
        <div className={`flex items-center gap-3 px-6 py-4 ${isHigh ? 'bg-red-50' : 'bg-blue-50'}`}>
          {isRejection ? (
            <AlertTriangle className={`w-7 h-7 flex-shrink-0 ${isHigh ? 'text-red-600' : 'text-yellow-500'}`} />
          ) : (
            <Bell className="w-7 h-7 flex-shrink-0 text-blue-600" />
          )}
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-semibold uppercase tracking-wide ${isHigh ? 'text-red-600' : 'text-blue-600'}`}>
              {isRejection ? 'Task Rejected' : 'Notification'}
              {queue.length > 1 && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-gray-200 text-gray-700">
                  {queue.length} pending
                </span>
              )}
            </p>
            <h2 className="text-lg font-bold text-gray-900 truncate">{current.title}</h2>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-3">
          {/* Message */}
          <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
            {current.message}
          </p>

          {/* Meta */}
          {(current.project_name || current.task_name) && (
            <div className="bg-gray-50 rounded-lg p-3 space-y-1 text-xs text-gray-600">
              {current.project_name && (
                <div><span className="font-medium">Project:</span> {current.project_name}</div>
              )}
              {current.task_name && (
                <div><span className="font-medium">Task:</span> {current.task_name}</div>
              )}
            </div>
          )}

          {/* Sender & time */}
          <div className="flex items-center justify-between text-xs text-gray-400">
            {current.sender_name && (
              <span>From: <span className="font-medium text-gray-600">{current.sender_name}</span></span>
            )}
            {current.created_at && (
              <span>
                {new Date(current.created_at.endsWith('Z') ? current.created_at : current.created_at + 'Z').toLocaleString('en-GB', {
                  day: '2-digit', month: 'short', year: 'numeric',
                  hour: '2-digit', minute: '2-digit'
                })}
              </span>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-t border-gray-100">
          {queue.length > 1 && (
            <p className="text-xs text-gray-500">
              {queue.length - 1} more notification{queue.length - 1 > 1 ? 's' : ''} waiting
            </p>
          )}
          <div className={queue.length > 1 ? '' : 'ml-auto'}>
            <button
              onClick={handleAcknowledge}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-semibold text-sm text-white transition-colors
                ${isHigh ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
            >
              <CheckCircle className="w-4 h-4" />
              OK, I understand
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationAlert;
